import React, { useEffect, useMemo } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LandingPageNavbar from "../LandingPage/LandingPageNavbar";
import LandingPageFooter from "../LandingPage/LandingPageFooter";
import { clearLearningHubArticle } from "../../rtk/slices/learningHubSlice";
import { fetchLearningHubArticle } from "../../rtk/thunks/learningHubThunk";
import { ArticleCard } from "./ArticleCard";
import { normalizeArticleMarkdown } from "./normalizeArticleMarkdown";
import "./LearningHub.css";

function LearningHubShell({ children }) {
    return (
        <>
            <LandingPageNavbar />
            {children}
            <LandingPageFooter />
        </>
    );
}

const formatArticleDate = (value) => {
    if (!value) return "";
    try {
        return new Date(value).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return "";
    }
};

export default function LearningHubArticle() {
    const { slug } = useParams();
    const dispatch = useDispatch();
    const { currentArticle, detailLoading, detailError } = useSelector(
        (state) => state.learningHub
    );

    useEffect(() => {
        if (!slug) return;
        dispatch(fetchLearningHubArticle(slug));
        return () => {
            dispatch(clearLearningHubArticle());
        };
    }, [dispatch, slug]);

    const markdownBody = useMemo(
        () => normalizeArticleMarkdown(currentArticle?.body),
        [currentArticle?.body]
    );

    const publishedLabel = formatArticleDate(currentArticle?.published_at);
    const updatedLabel = formatArticleDate(currentArticle?.updated_at);
    const showUpdated =
        currentArticle?.updated_at &&
        currentArticle?.published_at &&
        new Date(currentArticle.updated_at).toDateString() !==
            new Date(currentArticle.published_at).toDateString();

    return (
        <LearningHubShell>
            <main className="lh-page lh-article-page-wrap">
                <nav className="lh-breadcrumb" aria-label="Breadcrumb">
                    <NavLink to="/">Home</NavLink>
                    <span aria-hidden="true">›</span>
                    <NavLink to="/learning-hub">Learning Hub</NavLink>
                    {currentArticle?.title && (
                        <>
                            <span aria-hidden="true">›</span>
                            <span className="lh-breadcrumb-current">
                                {currentArticle.title}
                            </span>
                        </>
                    )}
                </nav>

                {detailLoading && <p className="lh-muted">Loading article…</p>}

                {detailError && !detailLoading && (
                    <div className="lh-alert" role="alert">
                        {typeof detailError === "string"
                            ? detailError
                            : detailError?.detail || "Article not found."}
                    </div>
                )}

                {currentArticle && !detailLoading && (
                    <article className="lh-article-shell">
                        {currentArticle.cover_image_url && (
                            <img
                                className="lh-hero"
                                src={currentArticle.cover_image_url}
                                alt={currentArticle.cover_image_alt || currentArticle.title}
                            />
                        )}
                        <div className="lh-article-inner">
                            {currentArticle.category?.name && (
                                <span className="lh-card-category">
                                    {currentArticle.category.name}
                                </span>
                            )}
                            <h1>{currentArticle.title}</h1>
                            <p className="lh-byline">
                                {currentArticle.author_name && (
                                    <span>
                                        By {currentArticle.author_name}
                                    </span>
                                )}
                                {currentArticle.reviewer_name && (
                                    <span>
                                        {currentArticle.author_name ? " · " : ""}
                                        Reviewed by {currentArticle.reviewer_name}
                                    </span>
                                )}
                                {!currentArticle.author_name &&
                                    !currentArticle.reviewer_name &&
                                    "Nexorah AI"}
                            </p>
                            {(publishedLabel || showUpdated) && (
                                <p className="lh-dates">
                                    {publishedLabel && (
                                        <span>Published {publishedLabel}</span>
                                    )}
                                    {showUpdated && updatedLabel && (
                                        <span> · Updated {updatedLabel}</span>
                                    )}
                                </p>
                            )}
                            {currentArticle.summary && (
                                <p className="lh-lead">{currentArticle.summary}</p>
                            )}
                            <div className="lh-body">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {markdownBody}
                                </ReactMarkdown>
                            </div>
                        </div>

                        {Array.isArray(currentArticle.related_articles) &&
                            currentArticle.related_articles.length > 0 && (
                                <section className="lh-related-section">
                                    <h2>Related articles</h2>
                                    <div className="lh-related-grid">
                                        {currentArticle.related_articles.map((item) => (
                                            <ArticleCard key={item.slug} article={item} />
                                        ))}
                                    </div>
                                </section>
                            )}

                        <section className="lh-explore-cta" aria-label="Explore Nexorah AI">
                            <h2>Explore Nexorah AI</h2>
                            <p>
                                Prepare for the CPHQ exam with structured video modules, exam-style
                                practice, mock exams, and progress tracking.
                            </p>
                            <div className="lh-explore-actions">
                                <Link to="/sign-up" className="lh-explore-btn">
                                    Get started
                                </Link>
                                <Link to="/" className="lh-explore-link">
                                    View the platform
                                </Link>
                            </div>
                        </section>
                    </article>
                )}
            </main>
        </LearningHubShell>
    );
}
